import { useState, useCallback, useEffect, useRef } from 'react';
import * as api from '../api/storageApi';
import type { DriveFolder } from '../types';

export interface TreeNode extends DriveFolder {
  children?: TreeNode[];
  loaded: boolean;
}

function syncExpanded(prefix: string): string[] {
  if (!prefix) return [];
  const parts = prefix.replace(/\/$/, '').split('/').filter(Boolean);
  const paths: string[] = [];
  let cur = '';
  for (const p of parts) {
    cur = cur ? `${cur}/${p}` : p;
    paths.push(cur);
  }
  return paths;
}

function findNode(nodes: TreeNode[], path: string): TreeNode | null {
  for (const n of nodes) {
    if (n.path === path) return n;
    const child = n.children ? findNode(n.children, path) : null;
    if (child) return child;
  }
  return null;
}

export function useTree(
  currentPrefix: string, 
  onRefreshNeeded?: () => void, 
  seedRoots?: DriveFolder[]
) {
  const [roots, setRoots] = useState<TreeNode[]>([]);
  // Inicializar expanded con los paths del currentPrefix (desde localStorage)
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const paths = syncExpanded(currentPrefix);
    return new Set(paths);
  });
  const didInitRef = useRef(false);
  const rootsRef = useRef<TreeNode[]>(roots);
  const mountedRef = useRef(true);
  const lazyLoadAbortRef = useRef<AbortController | null>(null);

  // Mantener rootsRef siempre actualizado
  useEffect(() => { rootsRef.current = roots; }, [roots]);

  // Cleanup on unmount
  useEffect(() => () => { mountedRef.current = false; }, []);

  const injectChildren = useCallback((path: string, children: TreeNode[]) => {
    const rec = (nodes: TreeNode[]): TreeNode[] =>
      nodes.map(n =>
        (n.path.replace(/\/$/, '') === path.replace(/\/$/, ''))
          ? { ...n, children, loaded: true }
          : { ...n, children: n.children ? rec(n.children) : n.children }
      );

    setRoots(prev => rec(prev));
  }, []);

  // Sync expanded with current navigation path
  useEffect(() => {
    const paths = syncExpanded(currentPrefix);
    if (paths.length) {
      setExpanded(prev => new Set([...prev, ...paths]));
    }
  }, [currentPrefix]);

  useEffect(() => {
    // Solo procesar si seedRoots está definido (puede ser array vacío)
    if (seedRoots === undefined) return;

    didInitRef.current = true;
    setRoots(prev => {
      // Evitar actualizaciones innecesarias comparando keys
      const prevKey = prev.map(p => p.path).join('|');
      const nextKey = seedRoots.map(p => p.path.replace(/\/$/, '')).join('|');
      if (prevKey === nextKey) return prev;
      
      // Actualizar roots (puede ser array vacío al inicio)
      return seedRoots.map(f => ({ ...f, path: f.path.replace(/\/$/, ''), loaded: false }));
    });
  }, [seedRoots]);

  // Ensure the chain of the currentPrefix exists in the tree (lazy load on navigation)
  useEffect(() => {
    if (!currentPrefix) return;
    if (rootsRef.current.length === 0) return;

    // Abortar lazy-load anterior si existe
    if (lazyLoadAbortRef.current) {
      lazyLoadAbortRef.current.abort();
      lazyLoadAbortRef.current = null;
    }
    
    const ctrl = new AbortController();
    lazyLoadAbortRef.current = ctrl;

    const paths = syncExpanded(currentPrefix);
    
    // Debounce: esperar 200ms antes de cargar (evita múltiples cargas rápidas)
    const timer = setTimeout(() => {
      (async () => {
        // Verificar si este controlador sigue activo antes de empezar
        if (ctrl.signal.aborted || !mountedRef.current) return;

        // Cargar TODOS los niveles no cargados en secuencia (de raíz a hoja)
        for (const path of paths) {
          if (ctrl.signal.aborted || !mountedRef.current) return;

          const normalizedPath = path.replace(/\/$/, '');
          const node = findNode(rootsRef.current, normalizedPath);
          
          // Si el nodo no existe o ya está cargado, continuar
          if (!node || node.loaded) continue;

          try {
            // Prioridad BAJA (2) para tree lazy-load - no debe interferir con navegación
            const result = await api.listFiles(normalizedPath, 500, ctrl.signal, 2);
            
            // Verificar si todavía es válido después de la llamada
            if (ctrl.signal.aborted || !mountedRef.current) return;
            
            const children: TreeNode[] = result.folders.map(f => ({ 
              ...f, 
              path: f.path.replace(/\/$/, ''), 
              loaded: false 
            }));
            injectChildren(normalizedPath, children);
            
            // Pequeña pausa entre cargas para no saturar
            await new Promise(resolve => setTimeout(resolve, 50));
          } catch (e) {
            // Ignorar AbortError silenciosamente
            if (e instanceof Error && e.name !== 'AbortError') {
              console.error(`❌ Tree lazy-load failed: ${normalizedPath}`, e);
            }
            // Si falla un nivel, detener la carga de niveles siguientes
            break;
          }
        }
      })();
    }, 200);

    return () => {
      clearTimeout(timer);
      ctrl.abort();
      if (lazyLoadAbortRef.current === ctrl) {
        lazyLoadAbortRef.current = null;
      }
    };
  }, [currentPrefix, injectChildren]);

  // Cargar carpetas raíz si no se pasaron seedRoots
  useEffect(() => {
    if (didInitRef.current) return;
    if (seedRoots !== undefined) return;
    
    didInitRef.current = true;
    (async () => {
      try {
        const result = await api.listFiles('', 500, undefined, 2);
        if (!mountedRef.current) return;
        const newRoots = result.folders.map(f => ({ ...f, path: f.path.replace(/\/$/, ''), loaded: false }));
        setRoots(newRoots);
        
        // Si hay un currentPrefix al inicio (desde localStorage), cargar toda la cadena inmediatamente
        if (currentPrefix) {
          const paths = syncExpanded(currentPrefix);
          // Cargar todos los niveles en secuencia
          for (const path of paths) {
            if (!mountedRef.current) break;
            try {
              const pathResult = await api.listFiles(path, 500, undefined, 1);
              if (!mountedRef.current) break;
              const children: TreeNode[] = pathResult.folders.map(f => ({ 
                ...f, 
                path: f.path.replace(/\/$/, ''), 
                loaded: false 
              }));
              injectChildren(path, children);
            } catch (e) {
              console.error(`❌ Tree initial path load failed: ${path}`, e);
              break;
            }
          }
        }
      } catch (e) {
        console.error('❌ Tree initial load failed', e);
      }
    })();
  }, [seedRoots, currentPrefix, injectChildren]);

  // Load children of a node (on user click)
  const expand = useCallback(async (path: string) => {
    const normalizedPath = path.replace(/\/$/, '');
    setExpanded(prev => new Set([...prev, normalizedPath]));

    const isLoaded = (nodes: TreeNode[]): boolean =>
      nodes.some(n => (n.path === normalizedPath ? n.loaded : isLoaded(n.children ?? [])));

    // Usar rootsRef para leer estado fresco
    if (isLoaded(rootsRef.current)) return;

    try {
      // Prioridad NORMAL (1) para expansión manual del usuario
      const result = await api.listFiles(normalizedPath, 500, undefined, 1);
      if (!mountedRef.current) return;
      const children: TreeNode[] = result.folders.map(f => ({ 
        ...f, 
        path: f.path.replace(/\/$/, ''), 
        loaded: false 
      }));
      injectChildren(normalizedPath, children);
    } catch (e) {
      if (e instanceof Error && e.name !== 'AbortError') {
        console.error(`❌ Tree expand failed: ${normalizedPath}`, e);
      }
    }
  }, [injectChildren]);

  const collapse = useCallback((path: string) => {
    const normalizedPath = path.replace(/\/$/, '');
    setExpanded(prev => { const next = new Set(prev); next.delete(normalizedPath); return next; });
  }, []);

  const toggle = useCallback((path: string) => {
    if (expanded.has(path)) collapse(path);
    else expand(path);
  }, [expanded, expand, collapse]);

  // Reload tree after mutations
  // opts.paths: si se especifica, solo se revalidan ESOS prefijos (carpetas padre afectadas).
  //             Si no, se revalida raíz + todos los expandidos (modo legacy).
  const refresh = useCallback(async (opts: { paths?: string[] } = {}) => {
    const { paths } = opts;
    try {
      if (paths && paths.length > 0) {
        // Refresh quirúrgico: solo los prefijos afectados.
        // Normalizar y deduplicar (raíz '' incluida si aplica).
        const targets = Array.from(new Set(paths.map(p => p.replace(/\/+$/, ''))));

        // Refrescar en paralelo con prioridad NORMAL (no bloquea navegación)
        await Promise.all(targets.map(async (path) => {
          try {
            const result = await api.listFiles(path, 500, undefined, 1);
            if (!mountedRef.current) return;
            const children: TreeNode[] = result.folders.map(f => ({
              ...f,
              path: f.path.replace(/\/$/, ''),
              loaded: false,
            }));
            if (path === '') {
              setRoots(children);
            } else {
              injectChildren(path, children);
            }
          } catch (e) {
            console.error(`❌ Tree selective refresh failed: ${path}`, e);
          }
        }));
      } else {
        // Modo legacy: refrescar raíz + todos los expandidos
        const expandedPaths = Array.from(expanded);
        const result = await api.listFiles('', 500, undefined, 0);
        if (!mountedRef.current) return;

        const newRoots = result.folders.map(f => ({ ...f, path: f.path.replace(/\/$/, ''), loaded: false }));
        setRoots(newRoots);

        for (const path of expandedPaths) {
          try {
            const childResult = await api.listFiles(path, 500, undefined, 0);
            if (!mountedRef.current) return;
            const children: TreeNode[] = childResult.folders.map(f => ({
              ...f,
              path: f.path.replace(/\/$/, ''),
              loaded: false,
            }));
            injectChildren(path, children);
          } catch (e) {
            console.error(`❌ Failed to refresh expanded node: ${path}`, e);
          }
        }
      }
    } catch (e) {
      console.error('❌ Tree refresh failed', e);
    }
    onRefreshNeeded?.();
  }, [expanded, injectChildren, onRefreshNeeded]);

  // Solo lazy loading - NO loadAll
  return { roots, expanded, toggle, refresh };
}
